function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// Rules Autopilot follows (/Autopilot/rules). Redesign 2026-10-09 (Rules.dc.html): four sections written as sentences
// with inline inputs, saved as you change them through PUT /autopilot/config (debounced). Managed workspaces never see
// the Money section (no spend, limits or margins). Changing rules is Administrator-only server side, so everyone else
// sees the same page read-only.
import React from "react";
import { withRouter } from "react-router";
import { Loader } from "../../components/loaders";
import PlunkDropDown from "../../components/plunk-dropdown";
import ServiceFactory from "../../service";
import { adBillingMode, ownAdAccounts } from "../../functions/tenant-features";
import { adSvc } from "./common";
import { autopilotSvc, cfgFromStatus, configBody, isAutopilotAdmin, CHANNELS, AUDIENCE_RULES, PERMISSION_OPTIONS, money } from "./autopilot-common";
const locSvc = api => {
  const S = ServiceFactory.getType(api, "product");
  return new S();
};
const safe = (p, fb) => p.then(d => d === undefined ? fb : d).catch(() => fb);
const asList = (d, key) => Array.isArray(d) ? d : d && (d[key] || d.items) || [];
const SAVE_DELAY = 700;
const PERMISSION_ROWS = [["create", "Start a new campaign"], ["update", "Adjust a running campaign (bids, ads, audience)"], ["pause", "Pause a campaign that's not working"], ["raiseCap", "Raise a spending limit"]];
const LANGUAGES = [["English", "English"], ["Spanish", "Spanish"], ["French", "French"], ["German", "German"], ["Portuguese", "Portuguese"], ["Italian", "Italian"]];
const languageName = v => ({
  en: "English",
  es: "Spanish",
  fr: "French",
  de: "German",
  pt: "Portuguese",
  it: "Italian"
})[String(v || "").toLowerCase()] || v || "English";
class AutopilotRules extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "load", async () => {
      const [status, places, providers] = await Promise.all([safe(autopilotSvc().getAsync(), null), safe(locSvc("/location/places").getAsync(), []), safe(adSvc("/provider-settings").getAsync(), [])]);
      if (!this._alive) return;
      const prov = {};
      asList(providers, "items").forEach(r => {
        const k = String(r.provider || "").toLowerCase();
        prov[k] = !!(r.clientId || r.accountId || r.connected || r.managedByPlunk);
      });
      const markets = asList(places, "places").map(m => typeof m === "string" ? m : m.name || m.place || m.market).filter(Boolean);
      this.setState({
        loading: false,
        status: status && typeof status === "object" ? status : null,
        cfg: cfgFromStatus(status),
        markets: [...new Set(markets)],
        providers: prov
      });
    });
    /** Change one value (dot path) and queue a save. */
    _defineProperty(this, "set", (path, v) => {
      if (!this.canEdit()) return;
      const next = JSON.parse(JSON.stringify(this.state.cfg));
      let o = next;
      const parts = path.split(".");
      for (let i = 0; i < parts.length - 1; i++) {
        o[parts[i]] = o[parts[i]] || {};
        o = o[parts[i]];
      }
      o[parts[parts.length - 1]] = v;
      this.setState({
        cfg: next,
        save: "pending"
      });
      if (this._timer) clearTimeout(this._timer);
      this._timer = setTimeout(() => {
        this._timer = null;
        this.flush();
      }, SAVE_DELAY);
    });
    _defineProperty(this, "flush", async () => {
      const cfg = this.state.cfg;
      if (!cfg) return;
      const seq = this._seq = (this._seq || 0) + 1;
      if (this._alive) this.setState({
        save: "saving"
      });
      try {
        await autopilotSvc("/config").putAsync(configBody(cfg));
        if (this._alive && seq === this._seq) this.setState({
          save: "saved"
        });
      } catch (e) {
        if (this._alive && seq === this._seq) this.setState({
          save: "error",
          saveError: e && e.message || ""
        });
      }
    });
    _defineProperty(this, "num", (path, raw, {
      min = 0,
      max
    } = {}) => {
      // Keep what's typed (so a box can be cleared and retyped); save only real numbers inside the range.
      this.setState({
        drafts: {
          ...(this.state.drafts || {}),
          [path]: raw
        }
      });
      const n = Number(String(raw).replace(/[$,%\s]/g, ""));
      if (String(raw).trim() === "" || !isFinite(n) || n < min || max != null && n > max) return;
      this.set(path, n);
    });
    _defineProperty(this, "endEdit", path => {
      const d = {
        ...(this.state.drafts || {})
      };
      delete d[path];
      this.setState({
        drafts: d
      });
    });
    this.state = {
      loading: true,
      status: null,
      cfg: null,
      markets: [],
      providers: {},
      save: "",
      addingDate: false,
      newDate: ""
    };
  }
  componentDidMount() {
    this._alive = true;
    this.load();
  }
  componentWillUnmount() {
    this._alive = false;
    // Leaving with a change still queued: send it now rather than drop it.
    if (this._timer) {
      clearTimeout(this._timer);
      this._timer = null;
      this.flush();
    }
  }
  managed() {
    return adBillingMode() === "managed" || !!(this.state.status && this.state.status.managed);
  }
  canEdit() {
    return isAutopilotAdmin();
  }
  saveState() {
    const {
      save
    } = this.state;
    if (!this.canEdit()) return /*#__PURE__*/React.createElement("span", {
      className: "apx-save ro"
    }, "View only");
    if (save === "pending" || save === "saving") return /*#__PURE__*/React.createElement("span", {
      className: "apx-save busy"
    }, "Saving\u2026");
    if (save === "error") return /*#__PURE__*/React.createElement("span", {
      className: "apx-save bad",
      title: this.state.saveError
    }, "Couldn't save. ", /*#__PURE__*/React.createElement("a", {
      href: "#",
      onClick: e => {
        e.preventDefault();
        this.flush();
      }
    }, "Try again"));
    return /*#__PURE__*/React.createElement("span", {
      className: "apx-save ok"
    }, "Saved");
  }
  numInput(path, value, label, {
    prefix = "",
    suffix = "",
    width = 60,
    min = 0,
    max
  } = {}) {
    const ro = !this.canEdit();
    const drafts = this.state.drafts || {};
    return /*#__PURE__*/React.createElement("span", {
      className: "apx-num",
      style: {
        width
      }
    }, prefix && /*#__PURE__*/React.createElement("i", null, prefix), /*#__PURE__*/React.createElement("input", {
      type: "number",
      inputMode: "numeric",
      "aria-label": label,
      min: min,
      max: max,
      disabled: ro,
      value: drafts[path] != null ? drafts[path] : value == null ? "" : value,
      onChange: e => this.num(path, e.target.value, {
        min,
        max
      }),
      onBlur: () => this.endEdit(path)
    }), suffix && /*#__PURE__*/React.createElement("i", null, suffix));
  }
  pick(value, options, onChange, width) {
    if (!this.canEdit()) {
      const o = options.find(([v]) => v === value);
      return /*#__PURE__*/React.createElement("span", {
        className: "apx-ro"
      }, o ? o[1] : value);
    }
    return /*#__PURE__*/React.createElement(PlunkDropDown, {
      width: width,
      value: value,
      options: options,
      onChange: onChange
    });
  }
  permissions() {
    const {
      cfg
    } = this.state;
    const pm = cfg.permissions;
    const g = cfg.guardrails;
    return /*#__PURE__*/React.createElement("section", {
      className: "apx-rsec"
    }, /*#__PURE__*/React.createElement("b", {
      className: "apx-rsec-t"
    }, "What it may do on its own"), PERMISSION_ROWS.map(([k, label]) => /*#__PURE__*/React.createElement("div", {
      key: k,
      className: "apx-rline"
    }, /*#__PURE__*/React.createElement("span", {
      className: "grow"
    }, label), k === "pause" ? /*#__PURE__*/React.createElement("span", {
      className: "apx-fixed"
    }, "Always, so problems stop fast") : k === "raiseCap" ? /*#__PURE__*/React.createElement("span", {
      className: "apx-fixed"
    }, "Never") : this.pick(pm[k] === "allow" ? "allow" : "ask", PERMISSION_OPTIONS.filter(([v]) => v !== "never"), v => this.set(`permissions.${k}`, v), "180px"))), /*#__PURE__*/React.createElement("div", {
      className: "apx-rsent"
    }, "No more than ", this.numInput("guardrails.maxNewDraftsPerWeek", g.maxNewDraftsPerWeek, "New drafts per week", {
      width: 56,
      max: 50
    }), " new drafts a week, and", " ", this.numInput("guardrails.maxAdjustmentsPerCampaignPerDay", g.maxAdjustmentsPerCampaignPerDay, "Changes per campaign per day", {
      width: 56,
      max: 20
    }), " change", Number(g.maxAdjustmentsPerCampaignPerDay) === 1 ? "" : "s", " per campaign a day"));
  }
  moneySection() {
    const {
      cfg,
      status
    } = this.state;
    const g = cfg.guardrails;
    const s = status && status.summary || {};
    const spent = Number(s.spendMonth) || 0;
    const cap = Number(g.monthlyCapUsd) || Number(s.capMonthly) || 0;
    const pct = cap > 0 ? Math.min(100, spent / cap * 100) : 0;
    return /*#__PURE__*/React.createElement("section", {
      className: "apx-rsec"
    }, /*#__PURE__*/React.createElement("b", {
      className: "apx-rsec-t"
    }, "Money"), /*#__PURE__*/React.createElement("div", {
      className: "apx-rsent"
    }, "Spend no more than ", this.numInput("guardrails.dailyCapUsd", g.dailyCapUsd, "Daily limit", {
      prefix: "$",
      width: 96
    }), " a day and", " ", this.numInput("guardrails.monthlyCapUsd", g.monthlyCapUsd, "Monthly limit", {
      prefix: "$",
      width: 110
    }), " a month across all ads"), /*#__PURE__*/React.createElement("div", {
      className: "apx-spent"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bar"
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        width: `${pct > 0 ? Math.max(1, pct) : 0}%`
      }
    })), /*#__PURE__*/React.createElement("span", null, money(spent), " spent this month", cap > 0 ? `, ${money(Math.max(0, cap - spent))} left` : "")), /*#__PURE__*/React.createElement("div", {
      className: "apx-rsent"
    }, "Skip products earning less than ", this.numInput("guardrails.marginFloorPct", g.marginFloorPct || 0, "Margin floor", {
      suffix: "%",
      width: 76,
      max: 100
    }), " margin"));
  }
  promotes() {
    const {
      cfg,
      markets
    } = this.state;
    const g = cfg.guardrails;
    const ro = !this.canEdit();
    const chosen = g.markets || [];
    const toggleMarket = m => this.set("guardrails.markets", chosen.includes(m) ? chosen.filter(x => x !== m) : [...chosen, m]);
    const lang = languageName(g.language);
    const langOpts = LANGUAGES.some(([v]) => v === lang) ? LANGUAGES : [...LANGUAGES, [lang, lang]];
    return /*#__PURE__*/React.createElement("section", {
      className: "apx-rsec"
    }, /*#__PURE__*/React.createElement("b", {
      className: "apx-rsec-t"
    }, "What it promotes and to whom"), /*#__PURE__*/React.createElement("div", {
      className: "apx-rsent"
    }, "Only products with at least ", this.numInput("guardrails.inventoryFloorDays", g.inventoryFloorDays, "Days of stock", {
      width: 60,
      max: 365
    }), " days of stock"), /*#__PURE__*/React.createElement("div", {
      className: "apx-rstack"
    }, /*#__PURE__*/React.createElement("span", null, "Who sees what"), this.pick(g.audienceRule, AUDIENCE_RULES, v => this.set("guardrails.audienceRule", v), "100%")), /*#__PURE__*/React.createElement("div", {
      className: "apx-rstack"
    }, /*#__PURE__*/React.createElement("span", null, "Where"), /*#__PURE__*/React.createElement("div", {
      className: "apx-chips"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: `apx-chip${chosen.length ? "" : " on"}`,
      disabled: ro,
      onClick: () => this.set("guardrails.markets", [])
    }, "All your markets"), markets.map(m => /*#__PURE__*/React.createElement("button", {
      type: "button",
      key: m,
      className: `apx-chip${chosen.includes(m) ? " on" : ""}`,
      disabled: ro,
      "aria-pressed": chosen.includes(m),
      onClick: () => toggleMarket(m)
    }, m))), !markets.length && /*#__PURE__*/React.createElement("span", {
      className: "apx-note"
    }, "You haven't set up markets yet. Autopilot works across all of them.")), /*#__PURE__*/React.createElement("div", {
      className: "apx-rline"
    }, /*#__PURE__*/React.createElement("span", {
      className: "grow"
    }, "Language for ads and emails"), this.pick(lang, langOpts, v => this.set("guardrails.language", v), "160px")));
  }
  channels() {
    const {
      cfg,
      providers,
      addingDate,
      newDate
    } = this.state;
    const g = cfg.guardrails;
    const ro = !this.canEdit();
    const connected = needs => !needs || needs === "google" && (providers.google || ownAdAccounts()) || providers[needs];
    const dates = [...(g.blackoutDates || [])].sort();
    const addDate = () => {
      const d = String(newDate || "").trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(d) && !dates.includes(d)) this.set("guardrails.blackoutDates", [...dates, d]);
      this.setState({
        addingDate: false,
        newDate: ""
      });
    };
    const fmt = d => {
      const x = new Date(`${d}T12:00:00`);
      return isNaN(x) ? d : x.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric"
      });
    };
    return /*#__PURE__*/React.createElement("section", {
      className: "apx-rsec"
    }, /*#__PURE__*/React.createElement("b", {
      className: "apx-rsec-t"
    }, "Channels and quiet dates"), /*#__PURE__*/React.createElement("div", {
      className: "apx-chips"
    }, CHANNELS.map(c => {
      const ok = connected(c.needs);
      const on = !!cfg.channels[c.key] && ok;
      return /*#__PURE__*/React.createElement("button", {
        type: "button",
        key: c.key,
        className: `apx-chip${on ? " on" : ""}${ok ? "" : " off"}`,
        disabled: ro || !ok,
        "aria-pressed": on,
        title: !ok ? "Not connected yet. Connect it in Campaigns settings." : c.sub || "",
        onClick: () => this.set(`channels.${c.key}`, !cfg.channels[c.key])
      }, on ? "✓ " : "", c.short || c.label, ok ? "" : " (not connected)");
    })), /*#__PURE__*/React.createElement("div", {
      className: "apx-rstack"
    }, /*#__PURE__*/React.createElement("span", null, "Don't start anything on these dates"), /*#__PURE__*/React.createElement("div", {
      className: "apx-chips"
    }, !dates.length && !addingDate && /*#__PURE__*/React.createElement("span", {
      className: "apx-note"
    }, "None yet"), dates.map(d => /*#__PURE__*/React.createElement("span", {
      key: d,
      className: "apx-chip on date"
    }, fmt(d), !ro && /*#__PURE__*/React.createElement("button", {
      type: "button",
      "aria-label": `Remove ${fmt(d)}`,
      onClick: () => this.set("guardrails.blackoutDates", dates.filter(x => x !== d))
    }, "\u2715"))), !ro && (addingDate ? /*#__PURE__*/React.createElement("span", {
      className: "apx-adddate"
    }, /*#__PURE__*/React.createElement("input", {
      type: "date",
      "aria-label": "Quiet date",
      value: newDate,
      autoFocus: true,
      onChange: e => this.setState({
        newDate: e.target.value
      }),
      onKeyDown: e => {
        if (e.key === "Enter") addDate();
        if (e.key === "Escape") this.setState({
          addingDate: false,
          newDate: ""
        });
      }
    }), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "apx-btn sm",
      disabled: !newDate,
      onClick: addDate
    }, "Add"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "apx-btn sm flat",
      onClick: () => this.setState({
        addingDate: false,
        newDate: ""
      })
    }, "Cancel")) : /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "apx-chip add",
      onClick: () => this.setState({
        addingDate: true
      })
    }, "+ Add a date")))));
  }
  render() {
    const {
      loading,
      cfg
    } = this.state;
    const back = e => {
      e.preventDefault();
      this.props.history.push("/Autopilot");
    };
    return /*#__PURE__*/React.createElement("div", {
      className: "apx-rules"
    }, /*#__PURE__*/React.createElement("div", {
      className: "apx-rtop"
    }, /*#__PURE__*/React.createElement("a", {
      href: "/Autopilot",
      className: "apx-back",
      "aria-label": "Back to Autopilot",
      onClick: back
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons",
      "aria-hidden": "true"
    }, "arrow_back")), /*#__PURE__*/React.createElement("div", {
      className: "apx-rtop-b"
    }, /*#__PURE__*/React.createElement("span", {
      className: "t"
    }, "Rules Autopilot follows"), /*#__PURE__*/React.createElement("span", {
      className: "s"
    }, "Every idea is checked against these before you see it \xB7 saved as you change them")), /*#__PURE__*/React.createElement("span", {
      className: "grow"
    }), !loading && cfg && this.saveState()), loading || !cfg ? /*#__PURE__*/React.createElement("div", {
      className: "apx-rbody"
    }, /*#__PURE__*/React.createElement(Loader, null)) : /*#__PURE__*/React.createElement("div", {
      className: "apx-rbody"
    }, !this.canEdit() && /*#__PURE__*/React.createElement("div", {
      className: "apx-ronly"
    }, "Only workspace owners and administrators can change these rules. Ask one of them if something should change."), /*#__PURE__*/React.createElement("div", {
      className: "apx-rgrid"
    }, this.permissions(), !this.managed() && this.moneySection(), this.promotes(), this.channels())));
  }
}
export default withRouter(AutopilotRules);